<template>
  <div class="filter-editor">
    <hr-filter-chip-group
      v-if="allowNegative"
      :model-value="modelValue.filterNegative"
      :options="negationOptions"
      @update:model-value="onNegationChange"
    />
    <!--
      Only for criteria whose value is a list, since an element with one value cannot be two
      things at once and "all" would match nothing.
    -->
    <hr-filter-chip-group
      v-if="supportsMatchMode(criterion)"
      :model-value="matchMode"
      :options="matchModeOptions"
      @update:model-value="emit('update:matchMode', $event)"
    />

    <!--
      The current selection sits above the list rather than only being marked inside it, so what
      the filter does is readable without scrolling a few hundred options.
    -->
    <div
      v-if="selected.length > 0"
      class="filter-editor__selection"
    >
      <hr-input-chip
        v-for="option in selectedOptions"
        :key="String(option.value)"
        size="sm"
        :label="option.label"
        :remove-aria-label="i18n.t('common.filter.buttonRemoveFilterLabel', { filter: option.label })"
        @remove="onToggle(option.value)"
      />
    </div>

    <hr-text-field
      :model-value="search"
      :label="i18n.t('common.buttonSearch')"
      clearable
      dense
      @update:model-value="search = $event ?? ''"
    />

    <div
      v-if="visibleOptions.length === 0"
      class="filter-editor__empty"
    >
      {{ i18n.t('common.filter.noMatchingOptions') }}
    </div>

    <hr-virtual-scroll
      v-else
      :items="visibleOptions"
      :virtual-scroll-item-size="40"
      class="filter-editor__list"
    >
      <!--
        A row of its own per option. The control's root is inline-flex, so left to itself a short
        option shares a line with the next one and the list reads as ragged columns; it also puts
        more than one option in a row the virtual scroll has sized for exactly one.
      -->
      <template #default="{ item }">
        <hr-checkbox
          :key="String(item.value)"
          :model-value="isSelected(item.value)"
          :disable="item.disable === true"
          class="filter-editor__row"
          @update:model-value="onToggle(item.value)"
        >
          <span class="filter-editor__option">
            <hr-country-flag
              v-if="item.flagIcon"
              :country="item.flagIcon"
            />
            <hr-icon
              v-else-if="item.icon"
              :name="item.icon"
              :color="item.color || 'primary'"
              size="sm"
            />
            {{ item.label }}
          </span>
        </hr-checkbox>
      </template>
    </hr-virtual-scroll>
  </div>
</template>

<script setup>
import { i18n } from 'boot/i18n';
import { computed, ref } from 'vue';

import {
  HrCheckbox, HrCountryFlag, HrFilterChipGroup, HrIcon, HrInputChip, HrTextField, HrVirtualScroll,
} from 'src/design-system';

import { MATCH_MODES, supportsMatchMode } from '../matchMode';

/**
 * Filters with more options than a chip row can carry, or whose options arrive asynchronously.
 *
 * Searchable and virtualised, because the largest of these is the country list at a couple of
 * hundred entries.
 */
defineOptions({ name: 'OptionListEditor' });

const props = defineProps({
  /** The criterion being edited. */
  criterion: {
    type: Object,
    required: true,
  },
  /** The statement being built for it. */
  modelValue: {
    type: Object,
    required: true,
  },
  /** Whether every chosen value must be present, or any one of them. */
  matchMode: {
    type: String,
    default: MATCH_MODES.ANY,
  },
});

const emit = defineEmits(['update:modelValue', 'update:matchMode']);

const search = ref('');

const options = computed(() => props.criterion.options?.filterCriteriaItems ?? []);

/** Criteria can offer an is / is not switch alongside the values. */
const allowNegative = computed(() => props.criterion.options?.allowNegative === true);

const negationOptions = computed(() => [
  { value: false, label: i18n.t('common.filter.filterPositive') },
  { value: true, label: i18n.t('common.filter.filterNegative') },
]);

const matchModeOptions = computed(() => [
  { value: MATCH_MODES.ANY, label: i18n.t('common.filter.matchAny') },
  { value: MATCH_MODES.ALL, label: i18n.t('common.filter.matchAll') },
]);

const selected = computed(() => (Array.isArray(props.modelValue.filteredItems)
  ? props.modelValue.filteredItems
  : []));

/*
 * Driven by the options rather than the stored values, so a selection whose option has since
 * disappeared is not rendered as a chip with no label.
 */
const selectedOptions = computed(() => options.value
  .filter((option) => selected.value.includes(option.value)));

const visibleOptions = computed(() => {
  const term = search.value.trim().toLowerCase();
  if (term === '') {
    return options.value;
  }
  return options.value.filter((option) => String(option.label ?? '').toLowerCase().includes(term));
});

const isSelected = (value) => selected.value.includes(value);

function onToggle(value) {
  emit('update:modelValue', {
    ...props.modelValue,
    filteredItems: isSelected(value)
      ? selected.value.filter((item) => item !== value)
      : [...selected.value, value],
  });
}

function onNegationChange(value) {
  emit('update:modelValue', { ...props.modelValue, filterNegative: value === true });
}
</script>

<style lang="scss" scoped>
.filter-editor {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-height: 0;
}

.filter-editor__selection {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.filter-editor__list {
  max-height: 40vh;
}

.filter-editor__row {
  display: flex;
}

/*
 * Block rather than inline, being the whole of the checkbox's label: inline-level it would hang on
 * that label's baseline and carry the line box's leading, making the row taller than the size the
 * virtual scroll is told to expect.
 */
.filter-editor__option {
  display: flex;
  align-items: center;
  gap: 8px;
}

.filter-editor__empty {
  color: var(--app-on-surface-variant);
}
</style>
